<template>
    <div>
        <!-- Modal -->
        <div
            class="modal fade"
            :class="{ show: modelValue }"
            :style="{ display: modelValue ? 'flex' : 'none' }"
            tabindex="-1"
        >
            <div class="modal-dialog" :class="modalClasses">
                <div class="modal-content">
                    <!-- Header -->
                    <div class="modal-header">
                        <slot name="header">
                            <slot name="title">
                                <h5 class="modal-title">{{ title }}</h5>
                            </slot>
                        </slot>

                        <button
                            type="button"
                            class="btn btn-outline-secondary rounded-pill m-0 px-2 py-1"
                            @click="close"
                        >
                            X
                        </button>
                    </div>

                    <!-- Body -->
                    <div class="modal-body">
                        <slot />
                    </div>

                    <!-- Footer -->
                    <div class="modal-footer d-flex align-items-center">
                        <button
                            v-if="showClose"
                            type="button"
                            class="btn btn-secondary m-0"
                            @click="close"
                        >
                            Close
                        </button>
                        <slot name="footer" />
                    </div>
                </div>
            </div>
        </div>

        <!-- Backdrop -->
        <div v-if="modelValue" class="modal-backdrop fade show"></div>
    </div>
</template>

<script setup>
import { computed, watch, ref } from "vue";

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
    showClose: {
        type: Boolean,
        default: true,
    },
    title: {
        type: String,
        default: "",
    },
    size: {
        type: String,
        default: "lg", // sm | lg | xl
    },
    refreshOnClose: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(["update:modelValue"]);

const previousValue = ref(props.modelValue);

// Watch for modal closing (when modelValue changes from true to false)
watch(() => props.modelValue, (newValue) => {
    // Removed page refresh on modal close - modals should not refresh the page
    previousValue.value = newValue;
});

const close = () => {
    emit("update:modelValue", false);
};

const modalClasses = computed(() => {
    const sizeClass = props.size === "2xl" ? "modal-2xl" : props.size ? `modal-${props.size}` : "";
    // Check if it's a quote modal - exclude from 90vh rule
    const isQuoteModal = props.title && (props.title.toLowerCase().includes("quote") || props.title.toLowerCase().includes("inspirational"));
    const quoteClass = isQuoteModal ? "modal-quote" : "";
    return [
        sizeClass,
        quoteClass,
        "modal-dialog-centered",
    ].filter(Boolean).join(" ");
});
</script>

<style>
    .modal-dialog {
        min-width: 90vw;
        min-height: 90vh;
        max-width: calc(100vw - 20px);
        max-height: calc(100vh - 20px);
        margin: 10px auto;
        display: flex;
        flex-direction: column;
    }
    
    .modal-content {
        min-height: 90vh;
        max-height: calc(100vh - 20px);
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }
    
    .modal-header {
        flex-shrink: 0;
    }
    
    .modal-body {
        flex: 1 1 auto;
        overflow-y: auto;
        overflow-x: hidden;
        min-height: 0;
        max-height: 100%;
    }
    
    .modal-footer {
        flex-shrink: 0;
    }
    
    /* Quote modals - exclude from 90vh rule */
    .modal-dialog.modal-quote, .modal-quote {
        min-width: auto;
        min-height: auto;
        max-width: 600px;
        max-height: auto;
        width: 600px;
    }
    
    .modal-quote .modal-content {
        min-height: auto;
        max-height: auto;
    }
    
    .modal-quote .modal-body {
        overflow-y: visible;
    }
    
    .modal-2xl {
        min-width: 90vw;
        min-height: 90vh;
        max-width: calc(100vw - 20px);
        max-height: calc(100vh - 20px);
    }
    
    .modal-sm,
    .modal-md,
    .modal-lg,
    .modal-xl {
        min-width: 90vw;
        min-height: 90vh;
        max-width: calc(100vw - 20px);
        max-height: calc(100vh - 20px);
    }
</style>